/**
 * TextStylePopover — toolbar flotante estilo Canva que aparece sobre cualquier
 * campo `<InlineEditable>` enfocado en el PlanBuilder.
 *
 * Cómo funciona:
 *   - Un listener global de `focusin` detecta cuando un `[contenteditable]`
 *     dentro del preview (.al-pb-preview) recibe focus
 *   - El popover se ancla con `position: fixed` arriba del campo
 *   - Lee `data-sid` y `data-fld` del elemento para identificar el campo
 *     en el plan
 *   - Botones B/I/U + alineación + color + tamaño llaman al EditorStyleContext
 *     que persiste el estilo en `section.styles[fieldPath]` (autosave)
 *   - Al blur del campo, popover se oculta (con delay para permitir click
 *     dentro del popover sin perder target)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useEditorStyle } from "./EditorStyleContext";
import type { FieldStyle } from "../../types/plan";

const SIZE_PRESETS = [
  { label: "S", em: "0.85em" },
  { label: "M", em: "1em" },
  { label: "L", em: "1.2em" },
  { label: "XL", em: "1.5em" },
];

const FONT_PRESETS = [
  { label: "Display", stack: "'Bricolage Grotesque', system-ui, sans-serif" },
  { label: "Body",    stack: "'Inter Tight', 'Inter', system-ui, sans-serif" },
  { label: "Serif",   stack: "'Playfair Display', Georgia, serif" },
  { label: "Modern",  stack: "'Montserrat', system-ui, sans-serif" },
  { label: "Mono",    stack: "'JetBrains Mono', ui-monospace, monospace" },
];

const COLOR_PRESETS = [
  "#0a0a0a",
  "#52525b",
  "#7c3aed",
  "#ec4899",
  "#06b6d4",
  "#f97316",
  "#10b981",
  "#fbbf24",
];

interface TextStylePopoverProps {
  /** Selector del contenedor donde se escucha focus (típicamente ".al-pb-preview"). */
  containerSelector: string;
}

export function TextStylePopover({ containerSelector }: TextStylePopoverProps) {
  const editorStyle = useEditorStyle();
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [showFonts, setShowFonts] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const blurTimer = useRef<number | null>(null);

  /** Devuelve sectionId/fieldPath del target actual (lee data-sid/data-fld). */
  const targetIds = (): { sectionId: string | null; fieldPath: string | null } => {
    if (!target) return { sectionId: null, fieldPath: null };
    return {
      sectionId: target.getAttribute("data-sid"),
      fieldPath: target.getAttribute("data-fld"),
    };
  };

  /** Style actual del campo (persistido en el plan). */
  const currentStyle = (): FieldStyle => {
    const { sectionId, fieldPath } = targetIds();
    if (!sectionId || !fieldPath) return {};
    return editorStyle.getFieldStyle(sectionId, fieldPath) || {};
  };

  const updatePos = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const popHeight = 44;
    const margin = 10;
    let top = rect.top - popHeight - margin;
    if (top < 8) top = rect.bottom + margin; // si no entra arriba, ponemos abajo
    const left = Math.max(
      8,
      Math.min(window.innerWidth - 420, rect.left + rect.width / 2 - 210),
    );
    setPos({ top, left });
  }, []);

  // Listener global de focusin / focusout en el contenedor del preview
  useEffect(() => {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const onFocusIn = (e: Event) => {
      const el = e.target as HTMLElement;
      if (!el || !el.isContentEditable) return;
      // No mostrar para input/textarea (ej. nombre del plan en topbar)
      const tag = el.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (blurTimer.current) {
        window.clearTimeout(blurTimer.current);
        blurTimer.current = null;
      }
      setTarget(el);
      updatePos(el);
    };

    const onFocusOut = (e: Event) => {
      const next = (e as FocusEvent).relatedTarget as HTMLElement | null;
      // Si el siguiente focus es DENTRO del popover, no cerrar
      if (next && popoverRef.current && popoverRef.current.contains(next)) return;
      blurTimer.current = window.setTimeout(() => {
        setTarget(null);
        setPos(null);
        setShowFonts(false);
        setShowColors(false);
      }, 200);
    };

    container.addEventListener("focusin", onFocusIn);
    container.addEventListener("focusout", onFocusOut);

    // Reposicionar al hacer scroll o resize
    const onLayout = () => {
      if (target) updatePos(target);
    };
    window.addEventListener("scroll", onLayout, true);
    window.addEventListener("resize", onLayout);

    return () => {
      container.removeEventListener("focusin", onFocusIn);
      container.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("scroll", onLayout, true);
      window.removeEventListener("resize", onLayout);
      if (blurTimer.current) window.clearTimeout(blurTimer.current);
    };
  }, [containerSelector, target, updatePos]);

  /**
   * Persiste un patch del style en el plan via context. El re-render de React
   * actualiza el span con el nuevo CSS. Si no hay sectionId/fieldPath
   * (campo sin identificar), cae al DOM directo como fallback no-persistente.
   */
  const apply = useCallback(
    (patch: Partial<FieldStyle>) => {
      if (!target) return;
      const { sectionId, fieldPath } = targetIds();
      if (sectionId && fieldPath) {
        editorStyle.patchFieldStyle(sectionId, fieldPath, patch);
      } else {
        // Fallback: aplicar al DOM (no persiste, pero al menos da feedback visual)
        for (const k in patch) {
          (target.style as any)[k] = (patch as any)[k];
        }
      }
      // Re-anclar por si el cambio de tamaño movió el campo
      requestAnimationFrame(() => updatePos(target));
    },
    [target, editorStyle, updatePos],
  );

  /** Toggle: si el style actual matchea `onValue`, lo quita; si no, lo aplica. */
  const toggle = useCallback(
    (key: keyof FieldStyle, onValue: string) => {
      if (!target) return;
      const cur = currentStyle();
      const isOn = cur[key] === onValue;
      apply({ [key]: isOn ? undefined : onValue } as Partial<FieldStyle>);
    },
    [target, apply],
  );

  const clearStyles = useCallback(() => {
    if (!target) return;
    const { sectionId, fieldPath } = targetIds();
    if (sectionId && fieldPath) {
      editorStyle.clearFieldStyle(sectionId, fieldPath);
    } else {
      target.removeAttribute("style");
    }
    setShowFonts(false);
    setShowColors(false);
  }, [target, editorStyle]);

  if (!target || !pos) return null;

  return (
    <div
      ref={popoverRef}
      className="al-tsp"
      // mousedown NO debería blurear el contenteditable
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 95,
      }}
    >
      <div className="al-tsp-row">
        {/* Bold */}
        <button
          className="al-tsp-btn"
          onClick={() => toggle("fontWeight", "700")}
          title="Negrita (Bold)"
          type="button"
        >
          <b>B</b>
        </button>
        {/* Italic */}
        <button
          className="al-tsp-btn"
          onClick={() => toggle("fontStyle", "italic")}
          title="Cursiva (Italic)"
          type="button"
        >
          <i>I</i>
        </button>
        {/* Underline */}
        <button
          className="al-tsp-btn"
          onClick={() => toggle("textDecoration", "underline")}
          title="Subrayado"
          type="button"
        >
          <u>U</u>
        </button>

        <div className="al-tsp-sep" />

        {/* Align */}
        <button className="al-tsp-btn" onClick={() => apply({ textAlign: "left" })} title="Alinear izquierda" type="button">
          <AlignIcon kind="left" />
        </button>
        <button className="al-tsp-btn" onClick={() => apply({ textAlign: "center" })} title="Centrar" type="button">
          <AlignIcon kind="center" />
        </button>
        <button className="al-tsp-btn" onClick={() => apply({ textAlign: "right" })} title="Alinear derecha" type="button">
          <AlignIcon kind="right" />
        </button>

        <div className="al-tsp-sep" />

        {/* Tamaño */}
        {SIZE_PRESETS.map((s) => (
          <button
            key={s.label}
            className="al-tsp-btn al-tsp-size"
            onClick={() => apply({ fontSize: s.em })}
            title={`Tamaño ${s.label}`}
            type="button"
          >
            {s.label}
          </button>
        ))}

        <div className="al-tsp-sep" />

        {/* Fuente */}
        <div style={{ position: "relative" }}>
          <button
            className="al-tsp-btn al-tsp-font-btn"
            onClick={() => { setShowFonts((v) => !v); setShowColors(false); }}
            title="Cambiar fuente"
            type="button"
          >
            Aa ▾
          </button>
          {showFonts && (
            <div className="al-tsp-dropdown">
              {FONT_PRESETS.map((f) => (
                <button
                  key={f.label}
                  className="al-tsp-dropdown-item"
                  onClick={() => { apply({ fontFamily: f.stack }); setShowFonts(false); }}
                  type="button"
                  style={{ fontFamily: f.stack }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Color */}
        <div style={{ position: "relative" }}>
          <button
            className="al-tsp-btn al-tsp-color-btn"
            onClick={() => { setShowColors((v) => !v); setShowFonts(false); }}
            title="Color del texto"
            type="button"
          >
            <span className="al-tsp-color-dot" style={{ background: currentStyle().color || target.style.color || "#0a0a0a" }} />
            ▾
          </button>
          {showColors && (
            <div className="al-tsp-dropdown al-tsp-color-grid">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  onClick={() => { apply({ color: c }); setShowColors(false); }}
                  type="button"
                  className="al-tsp-color-swatch"
                  style={{ background: c }}
                  title={c}
                />
              ))}
              <input
                type="color"
                onChange={(e) => apply({ color: e.target.value })}
                className="al-tsp-color-input"
                title="Color personalizado"
              />
            </div>
          )}
        </div>

        <div className="al-tsp-sep" />

        {/* Limpiar */}
        <button
          className="al-tsp-btn al-tsp-clear"
          onClick={clearStyles}
          title="Limpiar formato"
          type="button"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function AlignIcon({ kind }: { kind: "left" | "center" | "right" }) {
  const lines = kind === "left"
    ? [{ x: 3, w: 14 }, { x: 3, w: 10 }, { x: 3, w: 12 }]
    : kind === "center"
    ? [{ x: 3, w: 14 }, { x: 5, w: 10 }, { x: 4, w: 12 }]
    : [{ x: 3, w: 14 }, { x: 7, w: 10 }, { x: 5, w: 12 }];
  return (
    <svg width={14} height={12} viewBox="0 0 20 14" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round">
      {lines.map((l, i) => (
        <line key={i} x1={l.x} y1={2 + i * 5} x2={l.x + l.w} y2={2 + i * 5} />
      ))}
    </svg>
  );
}
